"use client";

import {
  Context,
  ContextContent,
  ContextContentBody,
  ContextContentHeader,
  ContextTrigger,
} from "@/components/ai-elements/context";
import { type ModelId } from "@/hooks/use-model";
import type { ChatUIMessage } from "@/types/chat";
import { getChatMessageUsage } from "@/types/chat";
import { useMemo } from "react";

const LARGE_MODEL_WARNING_THRESHOLD = 200_000;

const formatTokenCount = (value?: number) =>
  value === undefined
    ? "-"
    : new Intl.NumberFormat("en-US", { notation: "compact" }).format(value);

const DebugRow = ({
  label,
  value,
}: {
  label: string;
  value?: number;
}) => (
  <div className="flex items-center justify-between text-xs">
    <span className="text-muted-foreground">{label}</span>
    <span>{formatTokenCount(value)}</span>
  </div>
);

export const ChatUsageContext = ({
  className,
  contextTokens,
  contextWindow,
  metadata,
  modelId,
}: {
  className?: string;
  contextTokens?: number;
  contextWindow: number;
  metadata?: ChatUIMessage["metadata"];
  modelId: ModelId;
}) => {
  const usage = useMemo(() => getChatMessageUsage(metadata), [metadata]);
  const absoluteMax = contextWindow;
  const isLargeModel = absoluteMax > LARGE_MODEL_WARNING_THRESHOLD;
  const rawInputTokens = metadata?.inputTokens;
  const rawOutputTokens = metadata?.outputTokens;
  const cacheReadTokens = metadata?.cacheReadTokens;
  const cacheWriteTokens = metadata?.cacheWriteTokens;
  const adjustedTotalTokens = metadata?.totalTokens;
  const lastStepInTokens = metadata?.lastStepInputTokens;
  const lastStepOutTokens = metadata?.lastStepOutputTokens;

  // Total tokens from the last API call = input (already includes cache) + output.
  const hasLastStep = lastStepInTokens !== undefined || lastStepOutTokens !== undefined;
  const usedTokens = hasLastStep
    ? (lastStepInTokens ?? 0) + (lastStepOutTokens ?? 0)
    : (contextTokens ?? adjustedTotalTokens ?? 0);
  const isWarning = isLargeModel && usedTokens > LARGE_MODEL_WARNING_THRESHOLD;

  const hasUsageValues = [
    rawInputTokens,
    rawOutputTokens,
    adjustedTotalTokens,
    cacheReadTokens,
    cacheWriteTokens,
    lastStepInTokens,
    lastStepOutTokens,
    contextTokens,
  ].some((value) => value !== undefined);

  if (!hasUsageValues) {
    return null;
  }

  return (
    <Context
      isWarning={isWarning}
      maxTokens={absoluteMax}
      modelId={modelId}
      usage={usage}
      usedTokens={usedTokens}
    >
      <ContextTrigger className={className} />
      <ContextContent align="start" className="w-72">
        <ContextContentHeader />
        <ContextContentBody className="space-y-2">
          <p className="text-[11px] text-muted-foreground">Usage debug</p>
          <DebugRow label="Total input" value={rawInputTokens} />
          <DebugRow label="Total output" value={rawOutputTokens} />
          <DebugRow label="Total cache read" value={cacheReadTokens} />
          <DebugRow label="Total cache write" value={cacheWriteTokens} />
        </ContextContentBody>
      </ContextContent>
    </Context>
  );
};
